import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import {
  FiShield, FiPlus, FiEdit2, FiTrash2, FiX, FiSave,
  FiLock, FiAlertCircle, FiCheckSquare, FiRefreshCw, FiUsers,
} from 'react-icons/fi';
import { api } from '@crm/shared';
import { toast } from 'react-toastify';

/* ─── Design tokens ─── */
const C = {
  navy:'#0D3B5E', navyD:'#082B47', navyL:'#EBF4FF', navyM:'#1A5276',
  gold:'#F5A623', goldL:'#FEF3DC',
  green:'#16A34A', greenL:'#DCFCE7', greenB:'#86EFAC',
  red:'#DC2626', redL:'#FEF2F2', redB:'#FECACA',
  sky:'#0284C7', skyL:'#DBEAFE',
  purple:'#7C3AED', purpleL:'#EDE9FE',
  border:'#E5E7EB', borderL:'#F3F4F6', bg:'#F4F6F9',
  white:'#FFFFFF', t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

/* ─── Role badge colors ─── */
const ROLE_COLORS = {
  'Administrator':   { bg:C.redL,    color:C.red,    border:C.redB    },
  'Admin':           { bg:C.redL,    color:C.red,    border:C.redB    },
  'Sales Manager':   { bg:C.skyL,    color:C.sky,    border:'#BAE6FD' },
  'Team Lead':       { bg:C.purpleL, color:C.purple, border:'#C4B5FD' },
  'Sales Executive': { bg:C.greenL,  color:C.green,  border:C.greenB  },
  'Accountant':      { bg:C.goldL,   color:'#92670C',border:'#FDE68A' },
  'FOS':             { bg:'#FFF7ED', color:'#C2410C',border:'#FED7AA' },
};
const ROLE_DEFAULT = { bg:C.navyL, color:C.navy, border:'#BFDBFE' };
const roleStyle = (name) => ROLE_COLORS[name]||ROLE_DEFAULT;

/* ─── Shimmer ─── */
const Sh = ({ h=20, w='100%', r=6 }) => (
  <div style={{ height:h, width:w, borderRadius:r, background:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)', backgroundSize:'400px 100%', animation:'cfShimmer 1.4s infinite' }}/>
);

/* ─── KPI card ─── */
function KpiCard({ label, value, icon, color, colorL, loading }) {
  const [h, setH] = useState(false);
  return (
    <div onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'14px 16px 12px', transition:'all 0.17s', transform:h?'translateY(-2px)':'none', boxShadow:h?'0 8px 22px rgba(13,59,94,0.13)':'0 2px 6px rgba(13,59,94,0.06)', borderTop:`3px solid ${color}` }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
        <span style={{ fontSize:10.5, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em' }}>{label}</span>
        <div style={{ width:32, height:32, borderRadius:9, background:colorL||color+'22', display:'flex', alignItems:'center', justifyContent:'center', color, flexShrink:0 }}>{icon}</div>
      </div>
      {loading ? <Sh h={36} w='45%'/> : <div style={{ fontSize:30, fontWeight:800, color:C.t1, letterSpacing:'-0.05em', lineHeight:1 }}>{value}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   ROLES LIST
═══════════════════════════════════════════════════════ */
export default function RolesList() {
  const [roles,       setRoles]       = useState([]);
  const [modules,     setModules]     = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [apiError,    setApiError]    = useState(null);
  const [showModal,   setShowModal]   = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [deletingId,  setDeletingId]  = useState(null);
  const [confirmDel,  setConfirmDel]  = useState(null);
  const [formData,    setFormData]    = useState({ name:'', permission:[] });

  const extractArray = (res) => {
    const d = res?.data;
    if (Array.isArray(d?.data))    return d.data;
    if (Array.isArray(d?.results)) return d.results;
    if (Array.isArray(d))          return d;
    return [];
  };

  const fetchAll = async (isRefresh=false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      setApiError(null);
      const [rolesRes, modulesRes, permsRes] = await Promise.all([api.getRoles(), api.getModules(), api.getPermissions()]);
      setRoles(extractArray(rolesRes));
      setModules(extractArray(modulesRes));
      setPermissions(extractArray(permsRes));
    } catch (err) {
      const s = err?.status||err?.response?.status;
      setApiError(s===403?'Access denied (403). Your account has no role assigned.'
                 :s===401?'Session expired. Please log in again.'
                 :err?.message||'Failed to load roles.');
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => { setEditingRole(null); setFormData({ name:'', permission:[] }); setShowModal(true); };
  const openEdit   = (role) => { setEditingRole(role); setFormData({ name:role.name, permission:(role.permissions||[]).map(p=>p.id) }); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditingRole(null); setFormData({ name:'', permission:[] }); };

  const togglePermission = (permId) => {
    setFormData(prev => ({ ...prev, permission:prev.permission.includes(permId)?prev.permission.filter(id=>id!==permId):[...prev.permission,permId] }));
  };
  const toggleModule = (moduleId) => {
    const ids = permissions.filter(p => p.module?.id===moduleId).map(p => p.id);
    const allSelected = ids.every(id => formData.permission.includes(id));
    setFormData(prev => ({ ...prev, permission:allSelected?prev.permission.filter(id=>!ids.includes(id)):[...new Set([...prev.permission,...ids])] }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim())            { toast.error('Role name is required'); return; }
    if (formData.permission.length===0)   { toast.error('Select at least one permission'); return; }
    try {
      setSaving(true);
      const payload = { name:formData.name.trim(), permission_ids:formData.permission };
      if (editingRole) { await api.updateRole(editingRole.id, payload); toast.success('Role updated'); }
      else             { await api.createRole(payload);                 toast.success('Role created'); }
      closeModal(); fetchAll();
    } catch (err) { toast.error(err?.message||'Failed to save role'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (role) => {
    setDeletingId(role.id); setConfirmDel(null);
    try {
      await api.deleteRole(role.id); toast.success('Role deleted'); fetchAll();
    } catch { toast.error('Failed to delete role'); }
    finally  { setDeletingId(null); }
  };

  const groupedByModule = modules.map(mod => ({
    module:mod, perms:permissions.filter(p => p.module?.id===mod.id),
  }));

  return (
    <>
      <style>{`
        @keyframes cfShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes cfSpin    { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes cfFadeUp  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        .rl-card { transition:all 0.17s; }
        .rl-card:hover { transform:translateY(-2px); box-shadow:0 8px 22px rgba(13,59,94,0.13) !important; }
        .rl-btn { width:30px;height:30px;border-radius:8px;border:1.5px solid ${C.border};background:${C.white};display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all 0.13s; }
        .rl-btn:hover { transform:translateY(-1px);box-shadow:0 3px 8px rgba(0,0,0,0.10); }
        .rl-btn:disabled { opacity:0.4;cursor:not-allowed;transform:none; }
        .rl-mod:hover { background:rgba(13,59,94,0.04) !important; }
        .rl-perm:hover { background:${C.navyL} !important; }
      `}</style>
      <Layout pageTitle="Roles & Permissions">
        <div style={{ display:'flex', flexDirection:'column', gap:14, fontFamily:"'Plus Jakarta Sans',sans-serif", animation:'cfFadeUp 0.22s ease both' }}>

          {/* ── HERO BANNER ── */}
          <div style={{ borderRadius:14, padding:'20px 28px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:14, background:'linear-gradient(120deg,#0D3B5E,#1A5276)', boxShadow:'0 4px 20px rgba(13,59,94,0.20)', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div>
              <div style={{ fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.55)', marginBottom:4, letterSpacing:'0.06em', textTransform:'uppercase' }}>Management</div>
              <h2 style={{ margin:0, color:'#fff', fontSize:22, fontWeight:800, letterSpacing:'-0.03em', lineHeight:1.2 }}>Roles & Permissions</h2>
              <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.55)', marginTop:4 }}>Create roles and assign module-based permissions</div>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <button onClick={() => fetchAll(true)} disabled={refreshing} title="Refresh"
                style={{ width:38, height:38, borderRadius:10, background:'rgba(255,255,255,0.11)', border:'1px solid rgba(255,255,255,0.22)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.12s' }}
                onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.20)';}}
                onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.11)';}}>
                <FiRefreshCw size={15} style={{ animation:refreshing?'cfSpin 1s linear infinite':'none' }}/>
              </button>
              <button onClick={openCreate}
                style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'9px 20px', borderRadius:9, background:C.gold, color:'#0D1B2A', border:'none', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 2px 10px rgba(245,166,35,0.45)', transition:'all 0.12s' }}
                onMouseEnter={e=>{e.currentTarget.style.background='#FBBF4A';e.currentTarget.style.transform='translateY(-1px)';}}
                onMouseLeave={e=>{e.currentTarget.style.background=C.gold;e.currentTarget.style.transform='';}}>
                <FiPlus size={15}/> Create Role
              </button>
            </div>
          </div>

          {/* ── KPI CARDS ── */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
            <KpiCard label="Total Roles"       value={roles.length}       icon={<FiShield size={15}/>}      color={C.navy}   colorL={C.navyL}   loading={loading}/>
            <KpiCard label="Total Modules"     value={modules.length}     icon={<FiLock size={15}/>}         color={C.sky}    colorL={C.skyL}    loading={loading}/>
            <KpiCard label="Total Permissions" value={permissions.length} icon={<FiCheckSquare size={15}/>}  color={C.green}  colorL={C.greenL}  loading={loading}/>
          </div>

          {/* ── ROLES GRID ── */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, boxShadow:'0 2px 6px rgba(13,59,94,0.06)', overflow:'hidden' }}>
            <div style={{ padding:'12px 16px', borderBottom:`1px solid ${C.borderL}`, display:'flex', alignItems:'center', gap:8 }}>
              <FiShield size={14} style={{ color:C.navy, opacity:0.75 }}/>
              <span style={{ fontSize:13, fontWeight:700, color:C.t1 }}>All Roles</span>
              {!loading && <span style={{ background:C.navyL, color:C.navy, padding:'2px 8px', borderRadius:20, fontSize:10.5, fontWeight:700 }}>{roles.length}</span>}
            </div>

            {loading && (
              <div style={{ padding:16, display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:12 }}>
                {[1,2,3].map(i=><Sh key={i} h={120} r={12}/>)}
              </div>
            )}

            {!loading && apiError && (
              <div style={{ padding:'36px 24px', textAlign:'center' }}>
                <div style={{ display:'inline-block', maxWidth:520, textAlign:'left', background:C.redL, border:`1px solid ${C.redB}`, borderRadius:12, padding:'18px 22px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                    <FiAlertCircle size={16} style={{ color:C.red }}/>
                    <span style={{ fontWeight:700, color:C.red, fontSize:13 }}>Cannot Load Roles</span>
                  </div>
                  <p style={{ color:'#7F1D1D', fontSize:12.5, margin:'0 0 14px', lineHeight:1.6 }}>{apiError}</p>
                  <button onClick={() => fetchAll()} style={{ background:C.red, color:'#fff', border:'none', borderRadius:8, padding:'7px 16px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>Retry</button>
                </div>
              </div>
            )}

            {!loading && !apiError && roles.length===0 && (
              <div style={{ textAlign:'center', padding:'52px 24px' }}>
                <div style={{ width:60, height:60, borderRadius:'50%', background:C.navyL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                  <FiShield size={24} style={{ color:C.navy, opacity:0.5 }}/>
                </div>
                <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>No roles yet</div>
                <div style={{ fontSize:12.5, color:C.t4, marginBottom:16 }}>Click "Create Role" to add the first role</div>
                <button onClick={openCreate} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'9px 20px', background:C.gold, color:'#0D1B2A', border:'none', borderRadius:9, fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}><FiPlus size={14}/> Create Role</button>
              </div>
            )}

            {!loading && !apiError && roles.length>0 && (
              <div style={{ padding:16, display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:12 }}>
                {roles.map(role => {
                  const rs = roleStyle(role.name);
                  const permCount = Array.isArray(role.permissions)?role.permissions.length:0;
                  const isDel = deletingId===role.id;
                  return (
                    <div key={role.id} className="rl-card"
                      style={{ border:`1.5px solid ${rs.border}`, borderRadius:12, padding:'14px 16px', background:C.white, boxShadow:'0 2px 6px rgba(13,59,94,0.06)', opacity:isDel?0.5:1, borderTop:`3px solid ${rs.color}` }}>
                      {/* Role header */}
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:38, height:38, borderRadius:10, background:rs.bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                            <FiShield size={17} style={{ color:rs.color }}/>
                          </div>
                          <div>
                            <div style={{ fontWeight:800, fontSize:14, color:C.t1, letterSpacing:'-0.02em' }}>{role.name}</div>
                            <div style={{ fontSize:11.5, color:C.t4, marginTop:2 }}>{permCount} permission{permCount!==1?'s':''}</div>
                          </div>
                        </div>
                        <div style={{ display:'flex', gap:8 }}>
                          <button className="rl-btn" onClick={() => openEdit(role)} title="Edit" disabled={isDel} style={{ color:C.navy, borderColor:C.border }}><FiEdit2 size={13}/></button>
                          <button className="rl-btn" onClick={() => setConfirmDel(role)} title="Delete" disabled={isDel} style={{ color:C.red, borderColor:C.redB }}>
                            {isDel
                              ? <div style={{ width:12, height:12, borderRadius:'50%', border:`2px solid ${C.red}`, borderTopColor:'transparent', animation:'cfSpin 0.8s linear infinite' }}/>
                              : <FiTrash2 size={13}/>}
                          </button>
                        </div>
                      </div>
                      {/* Permission badges */}
                      <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                        {Array.isArray(role.permissions) && role.permissions.length>0
                          ? role.permissions.slice(0,6).map(p => (
                              <span key={p.id} style={{ display:'inline-block', padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:600, background:C.navyL, color:C.navy, border:`1px solid #C5D9EC`, whiteSpace:'nowrap' }}>
                                {p.module?.name} · {p.name}
                              </span>
                            ))
                          : <span style={{ fontSize:12, color:C.t4 }}>No permissions assigned</span>
                        }
                        {Array.isArray(role.permissions) && role.permissions.length>6 && (
                          <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:20, fontSize:10.5, fontWeight:700, background:C.border, color:C.t2 }}>+{role.permissions.length-6} more</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Layout>

      {/* ══ CREATE / EDIT MODAL ══ */}
      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(13,27,42,0.55)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, padding:16 }}
          onClick={e=>{ if(e.target===e.currentTarget) closeModal(); }}>
          <div style={{ background:C.white, borderRadius:16, width:'100%', maxWidth:660, maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 24px 60px rgba(13,27,42,0.35)', animation:'cfFadeUp 0.18s ease both' }}>
            {/* Header */}
            <div style={{ padding:'16px 22px', background:'linear-gradient(120deg,#0D3B5E,#1A5276)', borderRadius:'16px 16px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <div>
                <div style={{ fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.55)', marginBottom:3, textTransform:'uppercase', letterSpacing:'0.06em' }}>Roles & Permissions</div>
                <div style={{ fontSize:16, fontWeight:800, color:'#fff', letterSpacing:'-0.02em' }}>
                  {editingRole?`Edit Role: ${editingRole.name}`:'Create New Role'}
                </div>
              </div>
              <button onClick={closeModal} style={{ width:34, height:34, borderRadius:9, background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.22)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <FiX size={17}/>
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleSave} style={{ overflowY:'auto', flex:1 }}>
              <div style={{ padding:'20px 22px', display:'flex', flexDirection:'column', gap:18 }}>

                {/* Role name */}
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  <label style={{ fontSize:12, fontWeight:700, color:C.t2 }}>Role Name <span style={{ color:C.red }}>*</span></label>
                  <input type="text" placeholder="e.g. Sales Manager" value={formData.name}
                    onChange={e => setFormData({ ...formData, name:e.target.value })}
                    readOnly={!!editingRole}
                    style={{ height:40, border:`1.5px solid ${C.border}`, borderRadius:9, padding:'0 12px', fontSize:13, color:C.t1, background:'#FAFBFC', outline:'none', fontFamily:'inherit', boxSizing:'border-box', width:'100%', transition:'all 0.15s' }}
                    onFocus={e=>{e.target.style.borderColor=C.navy;e.target.style.background='#fff';e.target.style.boxShadow='0 0 0 3px rgba(13,59,94,0.08)';}}
                    onBlur={e=>{e.target.style.borderColor=C.border;e.target.style.background='#FAFBFC';e.target.style.boxShadow='none';}}
                    required/>
                  {editingRole && (
                    <div style={{ fontSize:11.5, color:C.t4, marginTop:4 }}>
                      Role Name is read only while editing.
                    </div>
                  )}
                </div>

                {/* Permissions */}
                <div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                    <label style={{ fontSize:12, fontWeight:700, color:C.t2 }}>Permissions <span style={{ color:C.red }}>*</span></label>
                    <span style={{ fontSize:11.5, fontWeight:700, background:C.navyL, color:C.navy, padding:'3px 10px', borderRadius:20 }}>{formData.permission.length} selected</span>
                  </div>

                  {groupedByModule.length===0
                    ? <div style={{ fontSize:12.5, color:C.t4, padding:'12px 0' }}>Loading permissions…</div>
                    : (
                      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                        {groupedByModule.map(({ module, perms }) => {
                          if (perms.length===0) return null;
                          const allChecked  = perms.every(p => formData.permission.includes(p.id));
                          const someChecked = perms.some(p => formData.permission.includes(p.id));
                          const selCount    = perms.filter(p => formData.permission.includes(p.id)).length;
                          return (
                            <div key={module.id} style={{ border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
                              <div className="rl-mod" style={{ padding:'9px 14px', background:'#F7F9FB', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:10, cursor:'pointer', transition:'background 0.12s' }}
                                onClick={() => toggleModule(module.id)}>
                                <input type="checkbox" checked={allChecked} ref={el=>{ if(el) el.indeterminate=someChecked&&!allChecked; }}
                                  onChange={() => toggleModule(module.id)} onClick={e=>e.stopPropagation()}
                                  style={{ width:15, height:15, cursor:'pointer', accentColor:C.navy }}/>
                                <span style={{ fontWeight:700, fontSize:11.5, textTransform:'uppercase', color:C.t2, letterSpacing:'0.06em', flex:1 }}>{module.name}</span>
                                <span style={{ fontSize:11.5, background:selCount>0?C.navyL:'#F3F4F6', color:selCount>0?C.navy:C.t4, padding:'2px 8px', borderRadius:20, fontWeight:700 }}>{selCount}/{perms.length}</span>
                              </div>
                              <div style={{ padding:'10px 14px', display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:6 }}>
                                {perms.map(perm => {
                                  const checked = formData.permission.includes(perm.id);
                                  return (
                                    <label key={perm.id} className="rl-perm"
                                      style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', fontSize:12.5, color:C.t1, padding:'5px 8px', borderRadius:7, background:checked?C.navyL:'transparent', transition:'background 0.12s' }}>
                                      <input type="checkbox" checked={checked} onChange={() => togglePermission(perm.id)} style={{ width:14, height:14, cursor:'pointer', accentColor:C.navy }}/>
                                      <span style={{ textTransform:'capitalize', fontWeight:checked?700:400 }}>{perm.name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )
                  }
                </div>
              </div>

              {/* Footer */}
              <div style={{ padding:'14px 22px', borderTop:`1px solid ${C.borderL}`, display:'flex', gap:10, justifyContent:'flex-end', background:'#FAFBFC', flexShrink:0 }}>
                <button type="button" onClick={closeModal} disabled={saving}
                  style={{ height:38, padding:'0 20px', border:`1px solid ${C.border}`, borderRadius:9, background:C.white, color:C.t2, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
                <button type="submit" disabled={saving}
                  style={{ height:38, padding:'0 24px', minWidth:150, background:saving?C.navyM:C.gold, color:saving?'#fff':'#0D1B2A', border:'none', borderRadius:9, fontSize:13, fontWeight:800, cursor:saving?'not-allowed':'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:7, boxShadow:'0 2px 8px rgba(245,166,35,0.35)', transition:'all 0.12s' }}>
                  {saving
                    ? <><div style={{ width:14, height:14, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.35)', borderTopColor:'#fff', animation:'cfSpin 0.8s linear infinite' }}/> Saving…</>
                    : <><FiSave size={14}/> {editingRole?'Update Role':'Create Role'}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══ DELETE CONFIRM ══ */}
      {confirmDel && (
        <div style={{ position:'fixed', inset:0, background:'rgba(13,27,42,0.55)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100, padding:16 }}
          onClick={e=>{ if(e.target===e.currentTarget) setConfirmDel(null); }}>
          <div style={{ background:C.white, borderRadius:16, width:'100%', maxWidth:400, boxShadow:'0 24px 60px rgba(13,27,42,0.35)', overflow:'hidden', animation:'cfFadeUp 0.18s ease both' }}>
            <div style={{ padding:'18px 22px', borderBottom:`1px solid ${C.borderL}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontWeight:800, fontSize:15, color:C.t1 }}>Delete Role</span>
              <button onClick={() => setConfirmDel(null)} style={{ background:'none', border:'none', cursor:'pointer', color:C.t4, display:'flex' }}><FiX size={18}/></button>
            </div>
            <div style={{ padding:'22px', textAlign:'center' }}>
              <div style={{ width:52, height:52, borderRadius:'50%', background:C.redL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                <FiShield size={22} style={{ color:C.red }}/>
              </div>
              <p style={{ fontSize:13.5, color:C.t2, margin:'0 0 6px', lineHeight:1.5 }}>
                Delete role <strong style={{ color:C.t1 }}>"{confirmDel.name}"</strong>?
              </p>
              <p style={{ fontSize:12, color:C.t4, margin:0 }}>Users assigned to this role may lose access. This cannot be undone.</p>
            </div>
            <div style={{ padding:'14px 22px', borderTop:`1px solid ${C.borderL}`, display:'flex', gap:10, justifyContent:'flex-end', background:'#FAFBFC' }}>
              <button onClick={() => setConfirmDel(null)} style={{ height:38, padding:'0 20px', border:`1px solid ${C.border}`, borderRadius:9, background:C.white, color:C.t2, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
              <button onClick={() => handleDelete(confirmDel)} style={{ height:38, padding:'0 22px', background:C.red, color:'#fff', border:'none', borderRadius:9, fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6, boxShadow:'0 2px 8px rgba(220,38,38,0.35)' }}>
                <FiTrash2 size={13}/> Delete Role
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
